import {QuantityRange} from '@hconnect/tuc-processes/fields'
import {useState, useEffect, useRef, useCallback} from 'react'

import {RangeEditableField} from '../components/EditableField'
import {IconButton} from '../components/IconButton'
import {QuantityDisplayValue} from '../components/QuantityDisplayValue'

import {useQuantityValidation} from './useQuantityValidation'

interface UseQuantityInputParams {
  value: number | null
  onChange: (value: number) => void
  range: QuantityRange
  defaultValue?: number
  disabled?: boolean
  onAutoCorrect?: (correction: {
    originalValue: number
    correctedValue: number
    reason: 'step' | 'min' | 'max'
  }) => void
}

type IconButtonProps = React.ComponentProps<typeof IconButton>
type DisplayValueProps = React.ComponentProps<typeof QuantityDisplayValue>
type EditableFieldProps = React.ComponentProps<typeof RangeEditableField>

interface UseQuantityInputReturn {
  isEditing: boolean
  incrementButtonProps: Pick<IconButtonProps, 'onClick' | 'disabled'>
  decrementButtonProps: Pick<IconButtonProps, 'onClick' | 'disabled'>
  displayValueProps: Pick<DisplayValueProps, 'value' | 'onValueClick'>
  editableFieldProps: Pick<
    EditableFieldProps,
    'inputRef' | 'value' | 'range' | 'onChange' | 'onBlur' | 'onKeyPress'
  >
}

/**
 * Custom hook that orchestrates quantity input handling.
 * Returns props objects ready to spread into subcomponents.
 * Uses useQuantityValidation for validation logic.
 */
export const useQuantityInput = ({
  value,
  onChange,
  range,
  disabled,
  onAutoCorrect
}: UseQuantityInputParams): UseQuantityInputReturn => {
  const {min, default: defaultValue} = range

  // Use validation hook for all mathematical operations
  const {validateAndAdjust, getNextValue, getPreviousValue, formatValue} = useQuantityValidation({
    range,
    value,
    onAutoCorrect
  })

  // Local state for the input field
  const [inputValue, setInputValue] = useState<string>(
    value?.toString() ?? defaultValue?.toString() ?? min.toString()
  )
  const [isEditing, setIsEditing] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const lastSyncedValueRef = useRef<number | null | undefined>(value)

  // Update local state when quantity from props changes.
  // Sync even during editing when external value changes (e.g. Discard Changes).
  useEffect(() => {
    const resolvedValue = value ?? defaultValue ?? min
    const hasExternalValueChanged = lastSyncedValueRef.current !== value

    if (hasExternalValueChanged || !isEditing) {
      setInputValue(resolvedValue.toString())
    }

    lastSyncedValueRef.current = value
  }, [value, defaultValue, min, isEditing])

  // Handle increment
  const handleIncrement = useCallback(() => {
    const currentValue = value ?? defaultValue ?? min
    const nextValue = getNextValue(currentValue)

    if (nextValue !== undefined) {
      onChange(nextValue)
    }
  }, [value, defaultValue, min, getNextValue, onChange])

  // Handle decrement
  const handleDecrement = useCallback(() => {
    const currentValue = value ?? defaultValue ?? min
    const prevValue = getPreviousValue(currentValue)

    if (prevValue !== undefined) {
      onChange(prevValue)
    }
  }, [value, defaultValue, min, getPreviousValue, onChange])

  // Handle input field changes
  const handleInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value)
  }, [])

  // Handle input field blur
  const handleInputBlur = useCallback(() => {
    setIsEditing(false)
    const numericValue = parseFloat(inputValue)

    if (isNaN(numericValue)) {
      const resetValue = value ?? defaultValue ?? min
      setInputValue(resetValue.toString())
      return
    }

    // Validate and adjust if needed
    const validatedValue = validateAndAdjust(numericValue, 'input')

    onChange(validatedValue)

    // Format with correct number of decimals
    const formattedValue = formatValue(validatedValue)
    setInputValue(formattedValue)
  }, [inputValue, value, defaultValue, min, validateAndAdjust, formatValue, onChange])

  // Handle Enter key press
  const handleKeyPress = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter') {
        inputRef.current?.blur()
      }
    },
    [inputRef]
  )

  // Handle click on value to start editing
  const handleValueClick = useCallback(() => {
    if (disabled) return
    setIsEditing(true)
    setTimeout(() => {
      inputRef.current?.focus()
      inputRef.current?.select()
    }, 0)
  }, [inputRef, disabled])

  // Format display value
  const displayValue = formatValue(value ?? defaultValue ?? min)

  return {
    isEditing,
    incrementButtonProps: {
      onClick: handleIncrement,
      disabled: disabled || value === null || getNextValue(value) === undefined
    },
    decrementButtonProps: {
      onClick: handleDecrement,
      disabled: disabled || value === null || getPreviousValue(value) === undefined
    },
    displayValueProps: {
      value: displayValue,
      onValueClick: handleValueClick
    },
    editableFieldProps: {
      inputRef,
      value: inputValue,
      range,
      onChange: handleInputChange,
      onBlur: handleInputBlur,
      onKeyPress: handleKeyPress
    }
  }
}
